import { useMemo, useCallback } from 'react';
import {
  useWorkItemsData,
  type UseWorkItemsDataOptions,
  type UseWorkItemsDataReturn,
  type WorkItemsDataPagination,
} from './useWorkItemsData';
import { useWorkItemsResolvedStatus, type ResolvedStatusEntry } from './useWorkItemsResolvedStatus';
import type { WorkItem } from '@/components/UniversalWorkItems';
import { normalizeWorkflowStatusKey } from '@/lib/work-items/operationalStatusConfig';

export interface WorkItemWithResolvedStatus extends WorkItem {
  resolved_status?: string;
  resolved_label?: string;
  resolved_source?: string;
  timing_status?: string | null;
  is_terminal?: boolean;
  is_operationally_complete?: boolean;
  workflow_state?: string | null;
  status_changed_at?: string | null;
}

interface UseWorkItemsWithResolvedStatusReturn {
  items: WorkItemWithResolvedStatus[];
  loading: boolean;
  isFetching: boolean;
  error: string | null;
  loadItems: () => Promise<void>;
  refreshItems: () => Promise<void>;
  resolvedStatuses: Record<string, ResolvedStatusEntry>;
  resolvedLoading: boolean;
  resolvedError: string | null;
  pagination: WorkItemsDataPagination | null;
}

/**
 * Wrapper around useWorkItemsData that also fetches resolved statuses
 * via the batch SSOT endpoint. This ensures list views display the
 * correct status including workflow state overlays.
 */
export function useWorkItemsWithResolvedStatus(
  options: UseWorkItemsDataOptions & { resolveStatuses?: boolean; statusRefreshIntervalMs?: number } = {}
): UseWorkItemsWithResolvedStatusReturn {
  const { resolveStatuses = false, statusRefreshIntervalMs, ...dataOptions } = options;

  const {
    items: rawItems,
    loading,
    isFetching,
    error,
    loadItems,
    refreshItems,
    pagination,
  } = useWorkItemsData(dataOptions);

  const ids = useMemo(() => rawItems.map((item) => item.id), [rawItems]);

  const {
    statuses: resolvedStatuses,
    loading: resolvedLoading,
    error: resolvedError,
    refetch: refetchResolvedStatuses,
  } = useWorkItemsResolvedStatus(ids, {
    enabled: resolveStatuses && ids.length > 0,
    refreshIntervalMs: statusRefreshIntervalMs,
  });

  const items = useMemo(() => {
    if (!resolveStatuses || Object.keys(resolvedStatuses).length === 0) {
      return rawItems as WorkItemWithResolvedStatus[];
    }

    return rawItems.map((item) => {
      const resolved = resolvedStatuses[item.id];
      if (!resolved) return item as WorkItemWithResolvedStatus;

      // Only override item.status with resolved_status when the resolved status
      // was computed from the same raw status. If item.status has changed since
      // the resolved status was fetched (e.g., after a status update), the
      // resolved status is stale and must not override the fresh raw status.
      const rawStatusMatches = normalizeWorkflowStatusKey(item.status) === resolved.raw_status;
      const effectiveStatus = rawStatusMatches
        ? (resolved.resolved_status !== item.status ? resolved.resolved_status : item.status)
        : item.status;

      return {
        ...item,
        resolved_status: resolved.resolved_status,
        resolved_label: resolved.resolved_label,
        resolved_source: resolved.source,
        timing_status: resolved.timing_status,
        is_terminal: resolved.is_terminal,
        is_operationally_complete: resolved.is_operationally_complete,
        workflow_state: resolved.workflow_state,
        status_changed_at: resolved.status_changed_at,
        status: effectiveStatus,
      } as WorkItemWithResolvedStatus;
    });
  }, [rawItems, resolvedStatuses, resolveStatuses]);

  // Combined refresh: await both raw items and resolved statuses so that
  // stale resolved statuses don't override fresh raw item statuses.
  const combinedRefreshItems = useCallback(async () => {
    await refreshItems();
    if (resolveStatuses) {
      await refetchResolvedStatuses();
    }
  }, [refreshItems, refetchResolvedStatuses, resolveStatuses]);

  return {
    items,
    loading,
    isFetching,
    error,
    loadItems,
    refreshItems: combinedRefreshItems,
    resolvedStatuses,
    resolvedLoading,
    resolvedError,
    pagination,
  };
}
